import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../constants/queryKeys';
import { ApiEndpoints } from '../../../constants/endpoints/apiEndpoints.ts';
import { baseQueryKeys } from '@cks-internal/react-base';
import { ActivityEndpoints } from '../../../constants/endpoints/activityEndpoints.ts';
import { CommunicationRecordTargetType } from '../../../globalModels/common/communicationRecordTargetType.ts';

export const useActivityQueryInvalidator = (activityId: number) => {
  const queryClient = useQueryClient();
  const statusEndpoints = [
    ActivityEndpoints.activityStatusSummary(activityId),
    ActivityEndpoints.activityBookingFlags(activityId),
    ActivityEndpoints.activityTravellerOptions(activityId),
    ActivityEndpoints.activityDuplicateGroup(activityId)
  ];

  const invalidEmailQueriesForActivity = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['entityCommunicationRecords', CommunicationRecordTargetType.Activity, activityId],
      type: 'all'
    });

    await queryClient.invalidateQueries({
      queryKey: [
        ...baseQueryKeys.querySelectOptions(
          ApiEndpoints.compose.get.attachments(CommunicationRecordTargetType.Activity, activityId)
        )
      ],
      type: 'all'
    });
  };

  const invalidateHotelSearchInformation = async () => {
    await queryClient.invalidateQueries({
      queryKey: [ActivityEndpoints.hotelSearch.searchInformation(activityId)],
      type: 'all'
    });
  };

  const invalidateAllQueriesForActivity = async () => {
    await queryClient.refetchQueries({
      queryKey: [QueryKeys.editActivity(activityId)],
      type: 'all'
    });
    await queryClient.refetchQueries({
      queryKey: ['entityCommunicationRecords', CommunicationRecordTargetType.Activity, activityId],
      type: 'all'
    });

    await queryClient.refetchQueries({
      queryKey: [
        ...baseQueryKeys.querySelectOptions(
          ApiEndpoints.compose.get.attachments(CommunicationRecordTargetType.Activity, activityId)
        )
      ],
      type: 'all'
    });

    await queryClient.invalidateQueries({
      queryKey: ['reconciliationEditorViewModel', activityId],
      type: 'all'
    });

    await invalidateHotelSearchInformation();
  };

  const invalidateStatusQuery = async () => {
    await Promise.all(
      statusEndpoints.map((endpoint) =>
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.editActivity(activityId), endpoint],
          type: 'all'
        })
      )
    );
  };

  const invalidateEndpointOnly = async (endpoint: string) => {
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.editActivity(activityId), endpoint],
      type: 'all'
    });
  };

  const invalidateRateDependentQueriesForActivity = async () => {
    await Promise.all([
      invalidateStatusQuery(),
      invalidateEndpointOnly(ActivityEndpoints.hotelRates.get(activityId)),
      invalidateHotelSearchInformation()
    ]);
  };

  return {
    invalidateAllQueriesForActivity,
    invalidateEndpointOnly,
    invalidateHotelSearchInformation,
    invalidateRateDependentQueriesForActivity,
    invalidateStatusQuery,
    invalidEmailQueriesForActivity,
    queryClient
  };
};
